import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    Row,
    Col,
} from "reactstrap";
import { Trash2 } from "react-feather";
import { toast } from "react-toastify";
import { pointTypeService } from "../../services/pointTypeService";
import MdiIconPicker from "../Common/MdiIconPicker";
import ConfirmDeleteDialog from "../Common/ConfirmDeleteDialog";

const MDI_CDN = "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg";
const mdiToSlug = (name) =>
    name
        .replace(/^mdi/, "")
        .replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`)
        .replace(/^-/, "");

const PointTypeModal = ({ isOpen, toggle, mode = "create", pointTypeData = null, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: "",
        enumValue: "",
        description: "",
        icon: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setConfirmingDelete(false);
            if (mode === "create") {
                setFormData({ name: "", enumValue: "", description: "", icon: "" });
            } else if ((mode === "view" || mode === "edit") && pointTypeData) {
                setFormData({
                    name: pointTypeData.name || "",
                    enumValue: pointTypeData.enumValue || "",
                    description: pointTypeData.description || "",
                    icon: pointTypeData.icon || "",
                });
            }
            setErrors({});
        }
    }, [isOpen, mode, pointTypeData]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Point type name is required";
        } else if (formData.name.length < 2) {
            newErrors.name = "Point type name must be at least 2 characters";
        }

        if (!formData.enumValue.trim()) {
            newErrors.enumValue = "Enum value is required";
        } else if (!/^[A-Z0-9_]+$/.test(formData.enumValue.trim())) {
            newErrors.enumValue = "Enum value must be uppercase letters, digits, and underscores only (e.g. AC_FAN)";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (mode === "view") {
            toggle();
            return;
        }

        if (!validateForm()) {
            toast.error("Please fix the form errors");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                name: formData.name.trim(),
                enumValue: formData.enumValue.trim(),
                description: formData.description.trim(),
                icon: formData.icon || "",
            };

            let response;
            if (mode === "create") {
                response = await pointTypeService.create(payload);
                toast.success("Point type created successfully");
            } else if (mode === "edit") {
                response = await pointTypeService.update(pointTypeData.id, payload);
                toast.success("Point type updated successfully");
            }
            if (onSuccess) onSuccess(response);
            toggle();
        } catch (error) {
            console.error(`Error ${mode === "create" ? "creating" : "updating"} point type:`, error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(`Failed to ${mode === "create" ? "create" : "update"} point type`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!pointTypeData) return;
        setDeleting(true);
        try {
            await pointTypeService.remove(pointTypeData.id);
            toast.success("Point type deleted successfully");
            if (onSuccess) onSuccess();
            toggle();
        } catch (error) {
            if (error.response?.data?.message) toast.error(error.response.data.message);
            else toast.error("Failed to delete point type");
        } finally {
            setDeleting(false);
            setConfirmingDelete(false);
        }
    };

    const getModalTitle = () => {
        switch (mode) {
            case "create": return "Create New Point Type";
            case "edit":   return "Edit Point Type";
            case "view":   return "Point Type Details";
            default:       return "Point Type";
        }
    };

    return (
        <>
        <Modal isOpen={isOpen && !confirmingDelete} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
            <>
                <ModalBody>
                    {(mode === "create" || mode === "edit") && (
                        <>
                            <FormGroup>
                                <Label for="name">
                                    Point Type Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder="Enter point type name"
                                    value={formData.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    invalid={!!errors.name}
                                />
                                <FormFeedback>{errors.name}</FormFeedback>
                            </FormGroup>

                            <FormGroup>
                                <Label for="enumValue">
                                    Enum Value <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="enumValue"
                                    type="text"
                                    placeholder="e.g. AC_FAN"
                                    value={formData.enumValue}
                                    onChange={(e) => handleChange("enumValue", e.target.value.toUpperCase())}
                                    invalid={!!errors.enumValue}
                                />
                                <FormFeedback>{errors.enumValue}</FormFeedback>
                                <small className="text-muted">
                                    Uppercase identifier used internally (e.g. AC_FAN, LIGHT_COLOR)
                                </small>
                            </FormGroup>

                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Input
                                    id="description"
                                    type="textarea"
                                    placeholder="Enter description"
                                    value={formData.description}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    rows="3"
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Icon</Label>
                                <MdiIconPicker
                                    value={formData.icon}
                                    onChange={(val) => handleChange("icon", val)}
                                    disabled={loading}
                                />
                                <small className="text-muted">
                                    Search from Material Design Icons (MDI) library
                                </small>
                            </FormGroup>

                        </>
                    )}

                    {mode === "view" && pointTypeData && (
                        <>
                            <h6 className="mb-3">Point Type Details</h6>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>ID</Label>
                                        <Input type="text" value={pointTypeData.id || "-"} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Name</Label>
                                        <Input type="text" value={pointTypeData.name || "-"} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label>Enum Value</Label>
                                        <Input
                                            type="text"
                                            value={pointTypeData.enumValue || "-"}
                                            disabled
                                            style={{ fontFamily: "monospace" }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label>Description</Label>
                                        <Input
                                            type="textarea"
                                            value={pointTypeData.description || "-"}
                                            disabled
                                            rows="3"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label>Icon</Label>
                                        {pointTypeData.icon ? (
                                            <div
                                                className="d-flex align-items-center gap-3 p-2 rounded"
                                                style={{ border: "1px solid #dee2e6", background: "#f8fafc" }}
                                            >
                                                <img
                                                    src={`${MDI_CDN}/${mdiToSlug(pointTypeData.icon)}.svg`}
                                                    width={32}
                                                    height={32}
                                                    alt={pointTypeData.icon}
                                                    onError={(e) => { e.target.style.display = "none"; }}
                                                />
                                                <code style={{ fontSize: "0.9rem", color: "#374151" }}>
                                                    {pointTypeData.icon}
                                                </code>
                                            </div>
                                        ) : (
                                            <Input type="text" value="-" disabled />
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Created At</Label>
                                        <Input
                                            type="text"
                                            value={new Date(pointTypeData.createdAt).toLocaleString()}
                                            disabled
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Updated At</Label>
                                        <Input
                                            type="text"
                                            value={new Date(pointTypeData.updatedAt).toLocaleString()}
                                            disabled
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    {mode === "view" ? (
                        <>
                            <Button
                                type="button"
                                color="danger"
                                outline
                                onClick={() => setConfirmingDelete(true)}
                                className="me-auto"
                            >
                                <Trash2 size={14} className="me-1" />
                                Delete
                            </Button>
                            <Button type="button" color="secondary" onClick={toggle}>
                                Close
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button type="button" color="secondary" onClick={toggle} disabled={loading}>
                                Cancel
                            </Button>
                            <Button color="primary" onClick={handleSubmit} disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {mode === "create" ? "Creating..." : "Updating..."}
                                    </>
                                ) : (
                                    mode === "create" ? "Create Point Type" : "Update Point Type"
                                )}
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </>
        </Modal>

        <ConfirmDeleteDialog
            isOpen={confirmingDelete}
            onClose={() => setConfirmingDelete(false)}
            onConfirm={handleDelete}
            deleting={deleting}
            title="Delete Point Type"
            itemName={pointTypeData?.name}
            warningText="Points using this type may be affected."
        />
        </>
    );
};

export default PointTypeModal;
